// חוזה Banner JSON — המקור היחיד לאמת שממנו המנוע מפיק HTML/CSS/JS.
// אין כאן שדה HTML חופשי או script. יחידות: פיקסלים ומילישניות.
import { z } from 'zod';

export const SCHEMA_VERSION = 1 as const;

const id = z.string().regex(/^[a-zA-Z0-9_-]{1,64}$/, 'מזהה לא תקין');
const hex = z.string().regex(/^#([0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/, 'צבע HEX לא תקין');
const px = z.number().finite().min(-5000).max(5000);
const ms = z.number().int().min(0).max(600_000);

export const Easing = z.enum(['linear', 'easeIn', 'easeOut', 'easeInOut']);
export type Easing = z.infer<typeof Easing>;

export const TextContent = z.object({
  text: z.string().max(500),
  direction: z.enum(['rtl', 'ltr', 'auto']),
  fontAssetId: z.string().nullable(), // null = פונט מערכת (מותר רק לטיוטה)
  fontFamily: z.string().max(100),
  fontWeight: z.number().int().min(100).max(900),
  fontSize: z.number().min(4).max(400),
  lineHeight: z.number().min(0.5).max(4),
  align: z.enum(['start', 'center', 'end']),
  verticalAlign: z.enum(['top', 'middle', 'bottom']).default('top'),
  color: hex,
  letterSpacing: z.number().min(-10).max(50).default(0),
  // מפתח לטקסט מאושר בקמפיין; אם locked — אסור למודל לשנות
  copyKey: z.string().max(64).nullable().default(null),
  locked: z.boolean().default(false),
});

export const ImageContent = z.object({
  assetId: z.string(),
  fit: z.enum(['contain', 'cover']),
  focalX: z.number().min(0).max(1).default(0.5),
  focalY: z.number().min(0).max(1).default(0.5),
});

export const ShapeContent = z.object({
  fill: hex,
  radius: z.number().min(0).max(1000).default(0),
  borderColor: hex.nullable().default(null),
  borderWidth: z.number().min(0).max(50).default(0),
});

export const ButtonContent = TextContent.extend({
  fill: hex,
  radius: z.number().min(0).max(1000).default(0),
});

const LayerBase = z.object({
  id,
  name: z.string().max(80),
  x: px, y: px,
  width: z.number().min(1).max(5000),
  height: z.number().min(1).max(5000),
  zIndex: z.number().int().min(0).max(999),
  rotation: z.number().min(-360).max(360).default(0),
  opacity: z.number().min(0).max(1).default(1),
  visible: z.boolean().default(true),
  locked: z.boolean().default(false),
  role: z.enum(['logo', 'headline', 'subline', 'cta', 'image', 'background', 'disclaimer', 'decor']).nullable().default(null),
  // מתי השכבה קיימת על הטיימליין (ביחס לתחילת לולאה)
  startMs: ms.default(0),
  endMs: ms.nullable().default(null), // null = עד סוף הבאנר
});

export const TextLayer = LayerBase.extend({ type: z.literal('text'), content: TextContent });
export const ImageLayer = LayerBase.extend({ type: z.literal('image'), content: ImageContent });
export const ShapeLayer = LayerBase.extend({ type: z.literal('shape'), content: ShapeContent });
export const ButtonLayer = LayerBase.extend({ type: z.literal('button'), content: ButtonContent });

export const Layer = z.discriminatedUnion('type', [TextLayer, ImageLayer, ShapeLayer, ButtonLayer]);
export type Layer = z.infer<typeof Layer>;
export type TextLayer = z.infer<typeof TextLayer>;
export type ImageLayer = z.infer<typeof ImageLayer>;
export type ButtonLayer = z.infer<typeof ButtonLayer>;

export const AnimProps = z.object({
  dx: z.number().min(-5000).max(5000).optional(),
  dy: z.number().min(-5000).max(5000).optional(),
  scale: z.number().min(0).max(20).optional(),
  opacity: z.number().min(0).max(1).optional(),
  rotation: z.number().min(-720).max(720).optional(),
});
export type AnimProps = z.infer<typeof AnimProps>;

export const Transition = z.object({
  type: z.enum(['fade', 'slide', 'scale', 'none']),
  from: z.enum(['left', 'right', 'top', 'bottom']).default('right'), // ל-slide
  distance: z.number().min(0).max(2000).default(40),
  durationMs: ms,
  easing: Easing.default('easeOut'),
});
export type Transition = z.infer<typeof Transition>;

export const Keyframe = z.object({ atMs: ms, props: AnimProps, easing: Easing.default('easeInOut') });

export const AnimationTrack = z.object({
  layerId: id,
  enter: Transition.nullable().default(null), // מתחיל ב-startMs של השכבה
  exit: Transition.nullable().default(null),  // מסתיים ב-endMs של השכבה
  keyframes: z.array(Keyframe).max(50).default([]),
});
export type AnimationTrack = z.infer<typeof AnimationTrack>;

export const Background = z.object({
  color: hex,
  assetId: z.string().nullable().default(null),
  fit: z.enum(['contain', 'cover']).default('cover'),
});

export const BannerDoc = z.object({
  schemaVersion: z.literal(SCHEMA_VERSION),
  width: z.number().int().min(10).max(4000),
  height: z.number().int().min(10).max(4000),
  durationMs: z.number().int().min(100).max(120_000), // אורך לולאה אחת
  loops: z.number().int().min(0).max(100), // 0 = אינסוף
  // הפריים שמוצג אחרי הלולאה האחרונה (ms בתוך הלולאה); null = סוף הלולאה
  endFrameMs: ms.nullable().default(null),
  background: Background,
  layers: z.array(Layer).max(60),
  animationTracks: z.array(AnimationTrack).max(60),
  assetIds: z.array(z.string()).max(100),
  meta: z.object({
    family: z.enum(['horizontal', 'square', 'vertical', 'thin']).optional(),
    note: z.string().max(500).optional(),
  }).default({}),
}).superRefine((doc, ctx) => {
  const ids = new Set<string>();
  for (const l of doc.layers) {
    if (ids.has(l.id)) ctx.addIssue({ code: 'custom', message: `מזהה שכבה כפול: ${l.id}`, path: ['layers'] });
    ids.add(l.id);
    if (l.endMs != null && l.endMs <= l.startMs) ctx.addIssue({ code: 'custom', message: `endMs לפני startMs בשכבה ${l.id}`, path: ['layers'] });
  }
  for (const t of doc.animationTracks) {
    if (!ids.has(t.layerId)) ctx.addIssue({ code: 'custom', message: `track לשכבה שלא קיימת: ${t.layerId}`, path: ['animationTracks'] });
  }
  const assetSet = new Set(doc.assetIds);
  for (const l of doc.layers) {
    const refs = referencedAssets(l);
    for (const a of refs) if (!assetSet.has(a)) ctx.addIssue({ code: 'custom', message: `נכס ${a} בשכבה ${l.id} לא מופיע ב-assetIds`, path: ['assetIds'] });
  }
  if (doc.background.assetId && !assetSet.has(doc.background.assetId)) {
    ctx.addIssue({ code: 'custom', message: 'נכס הרקע לא מופיע ב-assetIds', path: ['background'] });
  }
});
export type BannerDoc = z.infer<typeof BannerDoc>;

export function referencedAssets(l: Layer): string[] {
  if (l.type === 'image') return [l.content.assetId];
  if ((l.type === 'text' || l.type === 'button') && l.content.fontAssetId) return [l.content.fontAssetId];
  return [];
}

export function allReferencedAssets(doc: BannerDoc): string[] {
  const s = new Set<string>();
  for (const l of doc.layers) referencedAssets(l).forEach((a) => s.add(a));
  if (doc.background.assetId) s.add(doc.background.assetId);
  return [...s];
}

export type Family = 'horizontal' | 'square' | 'vertical' | 'thin';
/** משפחת קומפוזיציה לפי יחס ממדים */
export function familyOf(width: number, height: number): Family {
  const r = width / height;
  if (r >= 3.5 || r <= 1 / 3.5) return r >= 1 ? 'thin' : 'vertical';
  if (r >= 1.4) return 'horizontal';
  if (r <= 0.71) return 'vertical';
  return 'square';
}

export function parseBanner(input: unknown) {
  return BannerDoc.safeParse(input);
}
